import { Button } from '@mui/material'
import { BerthSide } from '@operations/app/api'
import { useOperationsStore } from '@operations/AppProvider'
import { useTranslate } from '@tolgee/react'
import { Header } from '@tom-ui/ui'
import { PATHS, useAsyncFetch } from '@tom-ui/utils'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router'
import { EquipmentPlanningUIStore } from '../../stores/equipment-planning-ui-store'
import { EquipmentPlanningBreadcrump } from './EquipmentPlanningBreadcrump'

interface Props {
  visitId?: number
  equipmentPlanningUIStore: EquipmentPlanningUIStore
}

export const CraneSplitHeader = observer(({ visitId, equipmentPlanningUIStore }: Props) => {
  const { t } = useTranslate()
  const { appStore } = useOperationsStore()
  const navigate = useNavigate()

  const title = t('craneSplit', 'Crane split')

  useAsyncFetch(async () => {
    equipmentPlanningUIStore.selectCranesDialogUIStore.toggle(false)
    equipmentPlanningUIStore.craneSplitMessageUIStore.setMessage()

    await equipmentPlanningUIStore.fetchData(visitId!)

    if (
      !equipmentPlanningUIStore.craneSplitContainerUIStore.calculatedBays.length ||
      !equipmentPlanningUIStore.selectCranesDialogUIStore.vesselVisit?.berthSide ||
      equipmentPlanningUIStore.selectCranesDialogUIStore.vesselVisit?.berthSide ===
        BerthSide.Unspecified
    )
      equipmentPlanningUIStore.selectCranesDialogUIStore.toggleWithRedirect()
    else if (
      equipmentPlanningUIStore.selectCranesDialogUIStore.vesselVisit?.berthSide !==
      equipmentPlanningUIStore.selectCranesDialogUIStore.vesselVisit?.craneSplitBerthSide
    )
      equipmentPlanningUIStore.selectCranesDialogUIStore.toggleWithRedirect(
        equipmentPlanningUIStore.craneSplitContainerUIStore.craneIds,
      )
  }, [visitId, equipmentPlanningUIStore])

  const handleSave = async () => {
    equipmentPlanningUIStore.craneSplitMessageUIStore.setMessage()
    const saveResponseMessage =
      await equipmentPlanningUIStore.craneSplitContainerUIStore.saveCraneSplit()

    if (saveResponseMessage) {
      equipmentPlanningUIStore.craneSplitMessageUIStore.setMessage(saveResponseMessage)
    } else {
      appStore.setShowAlert('success', t('craneSplitSaved', 'Crane split saved!'))
      if (
        !_(equipmentPlanningUIStore.craneSplitContainerUIStore.initialCraneIds).isEqual(
          equipmentPlanningUIStore.craneSplitContainerUIStore.craneIds,
        )
      ) {
        equipmentPlanningUIStore.craneSplitMessageUIStore.setMessage(
          t(
            'thereMayBeChangesToAssignedEquipmentPleaseReview',
            'There may be changes to assigned equipment. Please review to ensure everything is correct.',
          ),
          'warning',
        )
      }

      equipmentPlanningUIStore.craneSplitContainerUIStore.initInitialCranes()
    }
  }

  const handleCancel = async () => {
    if (equipmentPlanningUIStore.craneSplitContainerUIStore.canUndo) {
      equipmentPlanningUIStore.craneSplitDialogUIStore.toggleResetDialog()
    } else {
      const url = `${PATHS.visits.vesselVisits.url}/${visitId}/dashboard`

      navigate(url)
    }
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <EquipmentPlanningBreadcrump visitId={visitId} />

      <Header
        title={title}
        position='relative'
        rightRenderOption={
          <>
            {equipmentPlanningUIStore.craneSplitContainerUIStore.canModify && (
              <Button variant='text' color='secondary' onClick={handleCancel}>
                {t('cancel', 'Cancel')}
              </Button>
            )}
            {equipmentPlanningUIStore.craneSplitContainerUIStore.canModify && (
              <Button
                variant='contained'
                disabled={!equipmentPlanningUIStore.craneSplitContainerUIStore.canUndo}
                onClick={handleSave}
                data-cy='crane-split-header-save-btn'
              >
                {t('save', 'Save')}
              </Button>
            )}
          </>
        }
      />
    </>
  )
})
